import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, UncontrolledTooltip } from 'reactstrap';
import LabelGroup from 'components/Label-Group';
import TextInput from 'components/Text-Input';
import TextAreaInput from 'components/TextArea-Input';
import { ItemStyles } from './styles';

const ItemList = ({
  id,
  initialName,
  name,
  setName,
  initialDescription,
  description,
  setDescription,
  setItems,
  index,
  isNew,
  setSelectedItem,
  setIsOpenModalDelete,
  setStatus,
  addItem,
  updateItem,
  textSaveButton,
}) => {
  const { t: translate } = useTranslation();
  const [editMode, setEditMode] = useState(initialName === '');
  const [errors, setErrors] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDescription = typeof description !== 'undefined' && typeof setDescription === 'function';

  const handleCancel = () => {
    if (initialName === '') {
      setItems((prev) => prev.filter((_, i) => i !== index));
    } else {
      setEditMode(false);
      setName(initialName);
      if (hasDescription) setDescription(initialDescription);
      setErrors({});
    }
  };

  const handleDelete = async () => {
    try {
      setSelectedItem({ id, name, isNew: isNew || false });
      setIsOpenModalDelete(true);
    } catch (error) {
      console.error('Failed to delete column:', error);
      setSelectedItem({});
    }
  };

  const handleSave = async () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.title = { message: 'Required Field!' };
    } else if (!/[A-Za-z_]/.test(name)) {
      newErrors.title = { message: 'Title must contain at least a letter!' };
    }

    if (hasDescription) {
      if (!description.trim()) {
        newErrors.description = { message: 'Required Field!' };
      } else if (!/[A-Za-z_]/.test(description)) {
        newErrors.description = { message: 'Description must contain at least a letter!' };
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      let savedItem = hasDescription ? { id, name, description } : { id, name };

      if (isNew) {
        savedItem = await addItem();
        setStatus('added');
      } else {
        savedItem = await updateItem();
        setStatus('saved');
      }

      setItems((prev) => {
        const newItems = [...prev];
        newItems[index] = savedItem;
        return newItems;
      });

      setEditMode(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
    }
  };

  return (
    <ItemStyles
      className={`${editMode || !name ? 'align-items-start' : 'align-items-center'} ps-3`}
    >
      {editMode ? (
        <div className="w-100">
          <LabelGroup htmlFor="title" label="Title" mb={2} column required size={12}>
            <TextInput
              controlName="title"
              placeholder="Title"
              value={name}
              validation={{
                pattern: {
                  value: /[A-Za-z_]/,
                  message: 'Title must contain at least a letter!',
                },
              }}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.title) {
                  setErrors((prev) => {
                    const { title, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancel();
                }
              }}
              style={{
                borderColor: errors.title ? 'red' : undefined,
              }}
              maxLength={80}
              noEllipsis
              required
            />
            {errors.title && (
              <span className="item-list-error">{translate(errors.title.message)}</span>
            )}
          </LabelGroup>
          {hasDescription && (
            <LabelGroup htmlFor="description" label="Description" column required size={12}>
              <TextAreaInput
                controlName="description"
                placeholder="Description"
                value={description}
                validation={{
                  pattern: {
                    value: /[A-Za-z_]/,
                    message: 'Description must contain at least a letter!',
                  },
                }}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors((prev) => {
                      const { description, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSave();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancel();
                  }
                }}
                style={{
                  borderColor: errors.description ? 'red' : undefined,
                }}
                noEllipsis
                required
              />
              {errors.description && (
                <span className="item-list-error">{translate(errors.description.message)}</span>
              )}
            </LabelGroup>
          )}
          <div className="d-flex w-100 justify-content-between">
            {!isNew ? (
              <Button
                type="button"
                color="ghost"
                className="btn btn-delete w-auto"
                onClick={handleDelete}
              >
                {translate('Exclude')}
              </Button>
            ) : (
              <div></div>
            )}
            <div className="d-flex gap-2">
              <Button
                type="button"
                color="outline"
                className="btn btn-cancel w-auto"
                onClick={handleCancel}
              >
                {translate('Cancel')}
              </Button>
              <Button
                type="submit"
                color="primary"
                className="btn btn-save w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                {isNew && textSaveButton ? translate(textSaveButton) : translate('Save change')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column w-100">
          <div className="d-flex align-items-center justify-content-between w-100">
            <span className="w-100">{name}</span>
            <div className="d-flex gap-1 align-self-end">
              <Button
                color="ghost"
                className="btn-small"
                id={`${id}-btn-edit`}
                onClick={() => setEditMode(!editMode)}
              >
                <i className="bx bx-edit" style={{ fontSize: '1.25em', color: '#74788D' }} />
              </Button>
              <Button
                color="ghost"
                className="btn-small"
                id={`${id}-btn-delete`}
                onClick={handleDelete}
              >
                <i
                  className="bx bx-trash"
                  style={{ fontSize: '1.25em', color: 'rgba(244, 106, 106, 1)' }}
                />
              </Button>
              {hasDescription && (
                <Button
                  color="ghost"
                  className="btn-small"
                  id={`${id}-btn-toggle`}
                  onClick={() => setIsExpanded((prev) => !prev)}
                >
                  <i
                    className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`}
                    style={{ fontSize: '1.25em', color: '#74788D' }}
                  />
                </Button>
              )}
              <UncontrolledTooltip target={`${id}-btn-edit`} placement="top">
                {translate('Edit')}
              </UncontrolledTooltip>
              <UncontrolledTooltip target={`${id}-btn-delete`} placement="top">
                {translate('Exclude')}
              </UncontrolledTooltip>
            </div>
          </div>
          {hasDescription && isExpanded && (
            <div className="mt-2 w-100" style={{ color: '#6c757d' }}>
              {description}
            </div>
          )}
        </div>
      )}
    </ItemStyles>
  );
};

ItemList.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  initialDescription: PropTypes.string,
  description: PropTypes.string,
  setDescription: PropTypes.func,
  setItems: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isNew: PropTypes.bool,
  setSelectedItem: PropTypes.func.isRequired,
  setIsOpenModalDelete: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
  addItem: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  textSaveButton: PropTypes.string,
};

export default ItemList;
